// The game content and tile catalog, read from the files
// a world sends (the content bundle) or the seed the page ships. Content refers to other content by key in the files
// ("Log", "ElderBram") and by number everywhere else. `content()` is the content in effect; `useContent` swaps it.
import {
  ArmorSlot, Binding, GameConstants, GroundType, ItemQuality, JewelryKind, ObjectType, OffhandKind, QuestObjectiveKind, TalentArea, TalentStat, TalentTier,
  TileEntityType, TileLayer, ToolKind, WeaponClass,
} from './constants';
import { contentHash } from './hash';

/** What a ranged weapon shoots: speed in pixels per second, sprite drawn pointing up-right. */
export interface ProjectileStats { speed: number; radius: number; sprite: string; lightRadius: number; lightColor: string }

/**
 * A weapon: melee swings an arc; one with a projectile shoots it as far as its reach. Its class says what the offhand
 * slot may hold; a staff's own stats are unused (it casts the spell scroll in the offhand).
 */
export interface WeaponStats {
  weaponClass: WeaponClass;
  damage: number; reach: number; arcDegrees: number; cooldownSeconds: number; knockback: number;
  /** Mana per attack (0 = free) and the item each attack takes from the offhand slot (0 = none). */
  manaCost: number; ammo: number;
  projectile: ProjectileStats | null;
}

/**
 * A circle a spell marks on the ground (up to its reach away) instead of shooting: for `durationSeconds` it heals the
 * caster and their party standing in it.
 */
export interface HealZone { radius: number; durationSeconds: number; healPerSecond: number; color: string }

/**
 * A spell scroll's spell, cast by a staff: `missiles` projectiles fanned evenly across `spreadDegrees`, or (with a
 * zone and no projectile) a healing circle.
 */
export interface SpellStats {
  damage: number; reach: number; cooldownSeconds: number; knockback: number; manaCost: number;
  missiles: number; spreadDegrees: number;
  projectile: ProjectileStats | null;
  zone: HealZone | null;
}

export interface ItemDefinition {
  id: number;
  key: string;
  name: string;
  description: string;
  maxStack: number;
  quality: ItemQuality;
  binding: Binding;
  /** A sprite path (Sprites/<name>.png). */
  icon: string;
  tool: ToolKind;
  armor: ArmorSlot;
  armorValue: number;
  /** Armour that only counts while blocking (a shield's). */
  blockArmor: number;
  look: string;
  sellPrice: number;
  heals: number;
  weapon: WeaponStats | null;
  /** Offhand items (a spell scroll, arrows, a shield) go in the offhand slot beside a weapon that takes them. */
  offhand: OffhandKind;
  spell: SpellStats | null;
  /** Rings and necklaces go in the belt's jewellery slots. */
  jewelry: JewelryKind;
  /** Only one of this item may be equipped at a time. */
  unique: boolean;
}

export interface Ingredient { item: number; count: number }

export interface Recipe { id: number; station: TileEntityType; inputs: Ingredient[]; output: Ingredient }

export interface VendorItem { item: number; price: number }

export interface NpcDefinition {
  id: number; key: string; name: string; appearance: number; greeting: string; outfit: number[]; stock: VendorItem[];
}

export interface QuestObjective { kind: QuestObjectiveKind; target: number; count: number }

export interface QuestDefinition {
  id: number; key: string; title: string; description: string; giver: number; requires: number;
  objectives: QuestObjective[]; rewards: Ingredient[]; rewardCopper: number; rewardExp: number;
}

export interface LootDrop { item: number; min: number; max: number; chance: number }

export interface MobDefinition {
  type: number; key: string; name: string; sprite: string; tint: string; maxHealth: number; hitRadius: number;
  contactDamage: number; contactRange: number; contactCooldownSeconds: number; aggroRange: number; leashRange: number;
  chaseSpeed: number; wanderSpeed: number; hopSeconds: number; chaseHopInterval: number; loot: LootDrop[];
  baseExp: number; coinsMin: number; coinsMax: number;
}

export interface TalentEffect { stat: TalentStat; value: number }

/**
 * A node of the talent tree, at (x, y) in tree pixels. Links join both ways. An aura node's effects also reach party
 * members nearby.
 */
export interface TalentNode {
  id: number; key: string; name: string; description: string; area: TalentArea; tier: TalentTier;
  /** A sprite path drawn inside the node ('' = none). */
  icon: string;
  x: number; y: number; aura: boolean; links: number[]; effects: TalentEffect[];
}

export enum EntityBehaviour { Decorative = 0, Gather = 1 }

export interface EntityFootprint { x: number; y: number; width: number; height: number }
export interface EntityGather {
  yield: number; minYield: number; maxYield: number; tool: ToolKind;
  durationTicks: number; regrowTicks: number; verb: string;
  depletedSprite: string; removeCollisionWhenDepleted: boolean;
}
export interface EntityDefinition {
  id: number; key: string; name: string; sprite: string; animated: boolean;
  anchorX: number; anchorY: number; sortX: number; sortY: number;
  collision: EntityFootprint; behaviour: EntityBehaviour; gather: EntityGather | null;
  /** A light it gives off at night and in dark zones (0 = none), shining from (lightX, lightY) pixels from its placement
   * tile's top-left, moving with the art's offsets. */
  lightRadius: number; lightColor: string; lightX: number; lightY: number;
}

/** A collision rectangle in pixels from the top-left of the tile it belongs to (an object's: the tile it stands on). */
export interface CollisionRect { x: number; y: number; width: number; height: number }

export interface TileDefinition {
  id: number; name: string; layer: TileLayer; solid: boolean; animated: boolean; sprite: string; join: string; joinsWith: string[];
  baseGround?: number;
  /** A light it gives off at night and in dark zones: radius in pixels (0 = none), colour "#rrggbb", the row it shines from. */
  lightRadius: number; lightColor: string; lightY: number;
  /** Where across the tile the light shines from (pixels from its left; an object's may be anywhere on its art). */
  lightX: number;
  /** Objects only: flat ones lie on the ground under everything that stands; the rest are depth-sorted with characters
   * at sortY, pixels down from the top of the object's own tile (16 = its bottom edge). */
  flat: boolean; sortY: number;
  /** Objects only: touching it sets a player alight (Burning). */
  ignites?: boolean;
  /** What blocks: rectangles (an object's may reach anywhere on its art). Omitted: the whole tile if solid, else none. */
  collision?: CollisionRect[];
}

export const contentFiles = ['items.json', 'recipes.json', 'npcs.json', 'quests.json', 'mobs.json', 'settings.json', 'entities.json', 'talents.json'] as const;
export const tilesFile = 'tiles.json';

type Json = Record<string, unknown>;
const obj = (v: unknown): Json => (v && typeof v === 'object' && !Array.isArray(v) ? v as Json : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const num = (v: unknown, d = 0): number => (typeof v === 'number' ? v : d);
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
const bool = (v: unknown): boolean => v === true;

/** An enum written by name (JsonStringEnumConverter), or by number (a value with no name: custom tiles). */
function enumOf(e: Record<string, string | number>, v: unknown, d = 0): number {
  if (typeof v === 'number') return v;
  if (typeof v !== 'string') return d;
  const n = e[v] ?? Object.entries(e).find(([k]) => k.toLowerCase() === v.toLowerCase())?.[1];
  return typeof n === 'number' ? n : /^-?\d+$/.test(v) ? Number(v) : d;
}

/** Key → id of one list's entries (case-insensitive), read before resolving references. */
function scanKeys(doc: Json, list: string, idName: string): Map<string, number> {
  const keys = new Map<string, number>();
  for (const e of arr(doc[list])) {
    const key = str(obj(e).Key), id = obj(e)[idName];
    if (key && typeof id === 'number' && !keys.has(key.toLowerCase())) keys.set(key.toLowerCase(), id);
  }
  return keys;
}

/** A reference by key or number; "None", "" and unknown keys are 0 (unknown ones are reported). */
function ref(keys: Map<string, number>, v: unknown, what: string, problems: string[]): number {
  if (typeof v === 'number') return v;
  const text = str(v);
  if (!text || text === 'None') return 0;
  const id = keys.get(text.toLowerCase());
  if (id !== undefined) return id;
  if (/^-?\d+$/.test(text)) return Number(text);
  problems.push(`unknown ${what} '${text}'`);
  return 0;
}

function parseJson(text: string | undefined, file: string, problems: string[]): Json {
  if (text === undefined) return {};
  try { return obj(JSON.parse(text)); }
  catch (e) { problems.push(`${file}: ${(e as Error).message}`); return {}; }
}

export class TileCatalog {
  readonly tiles: TileDefinition[] = [];
  private readonly byKey = new Map<number, TileDefinition>();
  /** Collision that is exactly the whole tile (the fast path every older tile takes). */
  private solidGround = new Uint8Array(65536);
  private solidObject = new Uint8Array(65536);
  /** Any other collision: boxes [x0, y0, x1, y1] relative to the tile's top-left, and (objects) whether they reach
   * outside the tile, as [left, top, right, bottom] in tiles from it. */
  private partialGround: (Int16Array | null)[] = new Array(65536).fill(null);
  private partialObject: (Int16Array | null)[] = new Array(65536).fill(null);
  private spillObject: (Int16Array | null)[] = new Array(65536).fill(null);
  private ignitesObject = new Uint8Array(65536);
  private joinGroup = new Int32Array(65536);
  private joinMask: bigint[] = new Array(65536).fill(0n);

  constructor(tiles: TileDefinition[] = []) {
    this.tiles = tiles.filter(t => t.id >= 1 && t.id <= 65535);
    for (const t of this.tiles) this.byKey.set(t.layer * 65536 + t.id, t);
    this.solidGround[GroundType.Void] = 1; // outside the map / unloaded
    const groups = new Map<string, number>();
    const group = (name: string) => {
      const k = name.toLowerCase();
      let g = groups.get(k);
      if (g === undefined) groups.set(k, g = Math.min(groups.size + 1, 63));
      return g;
    };
    const ts = GameConstants.tileSize;
    for (const t of this.tiles) {
      const rects = t.collision && t.collision.length ? t.collision : t.solid ? [{ x: 0, y: 0, width: ts, height: ts }] : [];
      const whole = rects.length === 1 && rects[0].x === 0 && rects[0].y === 0 && rects[0].width === ts && rects[0].height === ts;
      const ground = t.layer === TileLayer.Ground;
      (ground ? this.solidGround : this.solidObject)[t.id] = whole ? 1 : 0;
      if (!whole && rects.length) {
        (ground ? this.partialGround : this.partialObject)[t.id] = Int16Array.from(rects.flatMap(r => [r.x, r.y, r.x + r.width, r.y + r.height]));
        const left = Math.min(...rects.map(r => Math.floor(r.x / ts))), top = Math.min(...rects.map(r => Math.floor(r.y / ts)));
        const right = Math.max(...rects.map(r => Math.ceil((r.x + r.width) / ts) - 1)), bottom = Math.max(...rects.map(r => Math.ceil((r.y + r.height) / ts) - 1));
        if (!ground && (left < 0 || top < 0 || right > 0 || bottom > 0)) this.spillObject[t.id] = Int16Array.of(left, top, right, bottom);
      }
      if (ground) continue;
      this.ignitesObject[t.id] = t.ignites ? 1 : 0;
      if (!t.join) continue;
      this.joinGroup[t.id] = group(t.join);
      let mask = 1n << BigInt(group(t.join));
      for (const other of t.joinsWith) mask |= 1n << BigInt(group(other));
      this.joinMask[t.id] = mask;
    }
  }

  static fromJson(text: string): TileCatalog {
    let doc: Json = {};
    try { doc = obj(JSON.parse(text)); } catch { /* an empty catalog */ }
    return new TileCatalog(arr(doc.Tiles).map(o => {
      const t = obj(o);
      return {
        id: num(t.Id), name: str(t.Name), layer: enumOf(TileLayer, t.Layer), solid: bool(t.Solid), animated: bool(t.Animated),
        sprite: str(t.Sprite), join: str(t.Join), joinsWith: arr(t.JoinsWith).map(x => str(x)), baseGround: num(t.BaseGround),
        lightRadius: num(t.LightRadius), lightColor: str(t.LightColor), lightX: num(t.LightX, 8), lightY: num(t.LightY, 8),
        flat: bool(t.Flat), sortY: num(t.SortY, 16), ignites: bool(t.Ignites),
        collision: arr(t.Collision).map(r => { const o = obj(r); return { x: num(o.X), y: num(o.Y), width: num(o.Width), height: num(o.Height) }; }),
      };
    }));
  }

  get(layer: TileLayer, id: number): TileDefinition | undefined { return this.byKey.get(layer * 65536 + id); }

  baseGround(g: number): number { return this.get(TileLayer.Ground, g)?.baseGround || g; }

  isSolidGround(g: number): boolean { return this.solidGround[g & 65535] === 1; }
  isSolidObject(o: number): boolean { return this.solidObject[o & 65535] === 1; }
  isSolid(g: number, o: number): boolean { return this.solidGround[g & 65535] === 1 || this.solidObject[o & 65535] === 1; }
  /** Whether touching this object sets a player alight. */
  ignites(o: number): boolean { return this.ignitesObject[o & 65535] === 1; }
  /** Custom collision of a tile, relative boxes [x0, y0, x1, y1]..., or null (none, or the whole tile). */
  groundBoxes(g: number): Int16Array | null { return this.partialGround[g & 65535]; }
  objectBoxes(o: number): Int16Array | null { return this.partialObject[o & 65535]; }
  /** How far an object's collision reaches past its tile, [left, top, right, bottom] in tiles, or null if it doesn't. */
  objectSpill(o: number): Int16Array | null { return this.spillObject[o & 65535]; }
  /** Appends a tile's custom boxes, placed at tile (tx, ty), in world pixels. */
  static place(boxes: Int16Array | null, tx: number, ty: number, out: number[]): void {
    if (!boxes) return;
    const x = tx * GameConstants.tileSize, y = ty * GameConstants.tileSize;
    for (let i = 0; i < boxes.length; i += 4) out.push(x + boxes[i], y + boxes[i + 1], x + boxes[i + 2], y + boxes[i + 3]);
  }

  /** Whether a joining object (a wall piece) joins the neighbour next to it. */
  joins(self: number, neighbour: number): boolean {
    const g = this.joinGroup[neighbour & 65535];
    return g !== 0 && (this.joinMask[self & 65535] & (1n << BigInt(g))) !== 0n;
  }

  nameOfGround(g: number): string { return this.get(TileLayer.Ground, g)?.name ?? (g === GroundType.Void ? 'Nothing' : `Ground #${g}`); }
  nameOfObject(o: number): string { return this.get(TileLayer.Object, o)?.name ?? (o === ObjectType.None ? 'None' : `Object #${o}`); }
}

/**
 * Objects whose collision reaches past their own tile (a house drawn with its footprint), indexed by every tile their
 * boxes touch, so a collision query at one tile finds them without scanning the neighbourhood.
 */
export class ObjectSpills {
  private readonly cells = new Map<number, number[]>();
  constructor(readonly width: number, readonly height: number) {}

  clear(): void { this.cells.clear(); }

  /** Indexes the object standing at (tx, ty), if its collision spills. */
  add(tx: number, ty: number, o: number, catalog: TileCatalog): void {
    const spill = catalog.objectSpill(o);
    if (!spill) return;
    for (let y = Math.max(0, ty + spill[1]); y <= Math.min(this.height - 1, ty + spill[3]); y++)
      for (let x = Math.max(0, tx + spill[0]); x <= Math.min(this.width - 1, tx + spill[2]); x++) {
        const key = y * this.width + x;
        let owners = this.cells.get(key);
        if (!owners) this.cells.set(key, owners = []);
        owners.push(tx, ty, o);
      }
  }

  /** Appends, in world pixels, the boxes of spilling objects that touch tile (tx, ty). */
  appendBoxes(tx: number, ty: number, catalog: TileCatalog, out: number[]): void {
    if (tx < 0 || ty < 0 || tx >= this.width || ty >= this.height) return;
    const owners = this.cells.get(ty * this.width + tx);
    if (owners) for (let i = 0; i < owners.length; i += 3) TileCatalog.place(catalog.objectBoxes(owners[i + 2]), owners[i], owners[i + 1], out);
  }
}

export const isPortalObject = (o: number) => o === ObjectType.CaveEntrance || o === ObjectType.CaveExit;
export const isLiquid = (g: number, catalog: TileCatalog) => {
  const base = catalog.baseGround(g);
  return base === GroundType.Water || base === GroundType.DeepWater || base === GroundType.Lava;
};

export class GameContent {
  readonly items = new Map<number, ItemDefinition>();
  readonly recipes = new Map<number, Recipe>();
  readonly npcs = new Map<number, NpcDefinition>();
  readonly quests = new Map<number, QuestDefinition>();
  readonly mobs = new Map<number, MobDefinition>();
  readonly entities = new Map<number, EntityDefinition>();
  readonly talents = new Map<number, TalentNode>();
  /** The tree's Start node (0: no tree). */
  talentStart = 0;
  startingItems: Ingredient[] = [];
  startingCopper = 0;
  startingLevel = 1;
  /** Unreadable files and unknown references found while reading. */
  readonly problems: string[] = [];
  /** The files it was read from (the bundle's texts), which the fingerprint hashes. */
  readonly files: ReadonlyMap<string, string>;
  readonly tiles: TileCatalog;
  /** What a client presents when connecting. */
  readonly fingerprint: number;

  constructor(files: ReadonlyMap<string, string>) {
    this.files = files;
    const p = this.problems;
    const docs = Object.fromEntries(contentFiles.map(f => [f, parseJson(files.get(f), f, p)])) as Record<(typeof contentFiles)[number], Json>;
    const itemKeys = scanKeys(docs['items.json'], 'Items', 'Id');
    const npcKeys = scanKeys(docs['npcs.json'], 'Npcs', 'Id');
    const mobKeys = scanKeys(docs['mobs.json'], 'Mobs', 'Type');
    const questKeys = scanKeys(docs['quests.json'], 'Quests', 'Id');
    const talentKeys = scanKeys(docs['talents.json'], 'Talents', 'Id');
    const item = (v: unknown) => ref(itemKeys, v, 'item', p);
    const ingredient = (v: unknown): Ingredient => ({ item: item(obj(v).Item), count: num(obj(v).Count) });

    for (const o of arr(docs['items.json'].Items)) {
      const i = obj(o), w = i.Weapon ? obj(i.Weapon) : null, sp = i.Spell ? obj(i.Spell) : null;
      const projectile = (v: unknown): ProjectileStats | null => {
        if (!v) return null;
        const pr = obj(v);
        return { speed: num(pr.Speed, 240), radius: num(pr.Radius, 2), sprite: str(pr.Sprite), lightRadius: num(pr.LightRadius), lightColor: str(pr.LightColor) };
      };
      const def: ItemDefinition = {
        id: num(i.Id), key: str(i.Key), name: str(i.Name), description: str(i.Description), maxStack: num(i.MaxStack, 1),
        quality: enumOf(ItemQuality, i.Quality, ItemQuality.Common), binding: enumOf(Binding, i.Binding), icon: str(i.Icon),
        tool: enumOf(ToolKind, i.Tool), armor: enumOf(ArmorSlot, i.Armor), armorValue: num(i.ArmorValue), blockArmor: num(i.BlockArmor), look: str(i.Look),
        sellPrice: num(i.SellPrice), heals: num(i.Heals),
        weapon: w && {
          weaponClass: enumOf(WeaponClass, w.Class), damage: num(w.Damage, 5), reach: num(w.Reach, 28), arcDegrees: num(w.ArcDegrees, 110),
          cooldownSeconds: num(w.CooldownSeconds, 0.45), knockback: num(w.Knockback, 26), manaCost: num(w.ManaCost), ammo: item(w.Ammo),
          projectile: projectile(w.Projectile),
        },
        offhand: enumOf(OffhandKind, i.Offhand),
        spell: sp && {
          damage: num(sp.Damage, 5), reach: num(sp.Reach, 128), cooldownSeconds: num(sp.CooldownSeconds, 1), knockback: num(sp.Knockback, 20),
          manaCost: num(sp.ManaCost), missiles: num(sp.Missiles, 1), spreadDegrees: num(sp.SpreadDegrees),
          projectile: sp.Zone ? null : projectile(sp.Projectile) ?? { speed: 240, radius: 2, sprite: '', lightRadius: 0, lightColor: '' },
          zone: sp.Zone ? { radius: num(obj(sp.Zone).Radius, 32), durationSeconds: num(obj(sp.Zone).DurationSeconds, 10),
            healPerSecond: num(obj(sp.Zone).HealPerSecond), color: str(obj(sp.Zone).Color, '#7fd99a') } : null,
        },
        jewelry: enumOf(JewelryKind, i.Jewelry), unique: bool(i.Unique),
      };
      if (!this.items.has(def.id)) this.items.set(def.id, def);
    }
    for (const o of arr(docs['recipes.json'].Recipes)) {
      const r = obj(o);
      const recipe: Recipe = {
        id: num(r.Id), station: enumOf(TileEntityType, r.Station, TileEntityType.Workbench),
        inputs: arr(r.Inputs).map(ingredient), output: ingredient(r.Output),
      };
      if (!this.recipes.has(recipe.id)) this.recipes.set(recipe.id, recipe);
    }
    for (const o of arr(docs['npcs.json'].Npcs)) {
      const n = obj(o);
      const npc: NpcDefinition = {
        id: num(n.Id), key: str(n.Key), name: str(n.Name), appearance: num(n.Appearance), greeting: str(n.Greeting),
        outfit: arr(n.Outfit).map(item), stock: arr(n.Stock).map(s => ({ item: item(obj(s).Item), price: num(obj(s).Price) })),
      };
      if (!this.npcs.has(npc.id)) this.npcs.set(npc.id, npc);
    }
    for (const o of arr(docs['quests.json'].Quests)) {
      const q = obj(o);
      const quest: QuestDefinition = {
        id: num(q.Id), key: str(q.Key), title: str(q.Title), description: str(q.Description),
        giver: ref(npcKeys, q.Giver, 'NPC', p), requires: ref(questKeys, q.Requires, 'quest', p),
        objectives: arr(q.Objectives).map(x => {
          const ob = obj(x);
          const kind = enumOf(QuestObjectiveKind, ob.Kind, QuestObjectiveKind.Collect);
          const keys = kind === QuestObjectiveKind.Kill ? mobKeys : kind === QuestObjectiveKind.Talk ? npcKeys : itemKeys;
          const what = kind === QuestObjectiveKind.Kill ? 'mob' : kind === QuestObjectiveKind.Talk ? 'NPC' : 'item';
          return { kind, target: ref(keys, ob.Target, what, p), count: num(ob.Count, 1) };
        }),
        rewards: arr(q.Rewards).map(ingredient), rewardCopper: num(q.RewardCopper), rewardExp: num(q.RewardExp),
      };
      if (!this.quests.has(quest.id)) this.quests.set(quest.id, quest);
    }
    for (const o of arr(docs['mobs.json'].Mobs)) {
      const m = obj(o);
      const mob: MobDefinition = {
        type: num(m.Type), key: str(m.Key), name: str(m.Name), sprite: str(m.Sprite, 'Sprites/slime.png'), tint: str(m.Tint, '#ffffff'),
        maxHealth: num(m.MaxHealth, 10), hitRadius: num(m.HitRadius, 6.5), contactDamage: num(m.ContactDamage),
        contactRange: num(m.ContactRange, 10), contactCooldownSeconds: num(m.ContactCooldownSeconds, 1),
        aggroRange: num(m.AggroRange), leashRange: num(m.LeashRange), chaseSpeed: num(m.ChaseSpeed, 50),
        wanderSpeed: num(m.WanderSpeed, 25), hopSeconds: num(m.HopSeconds, 0.35), chaseHopInterval: num(m.ChaseHopInterval, 0.7),
        loot: arr(m.Loot).map(l => ({ item: item(obj(l).Item), min: num(obj(l).Min), max: num(obj(l).Max), chance: num(obj(l).Chance) })),
        baseExp: num(m.BaseExp), coinsMin: num(m.CoinsMin), coinsMax: num(m.CoinsMax),
      };
      if (!this.mobs.has(mob.type)) this.mobs.set(mob.type, mob);
    }
    for (const o of arr(docs['entities.json'].Entities)) {
      const e = obj(o), c = obj(e.Collision), g = e.Gather ? obj(e.Gather) : null;
      const definition: EntityDefinition = {
        id: num(e.Id), key: str(e.Key), name: str(e.Name), sprite: str(e.Sprite), animated: bool(e.Animated),
        anchorX: num(e.AnchorX, 8), anchorY: num(e.AnchorY, 16), sortX: num(e.SortX, 8), sortY: num(e.SortY, 16),
        collision: { x: num(c.X), y: num(c.Y), width: num(c.Width), height: num(c.Height) },
        behaviour: enumOf(EntityBehaviour, e.Behaviour),
        lightRadius: num(e.LightRadius), lightColor: str(e.LightColor), lightX: num(e.LightX, 8), lightY: num(e.LightY, 8),
        gather: g && {
          yield: item(g.Yield), minYield: num(g.MinYield, 1), maxYield: num(g.MaxYield, 1), tool: enumOf(ToolKind, g.Tool),
          durationTicks: num(g.DurationTicks, 30), regrowTicks: num(g.RegrowTicks, 300), verb: str(g.Verb, 'Gather'),
          depletedSprite: str(g.DepletedSprite), removeCollisionWhenDepleted: g.RemoveCollisionWhenDepleted !== false,
        },
      };
      if (!this.entities.has(definition.id)) this.entities.set(definition.id, definition);
    }
    for (const o of arr(docs['talents.json'].Talents)) {
      const n = obj(o);
      const node: TalentNode = {
        id: num(n.Id), key: str(n.Key), name: str(n.Name), description: str(n.Description), area: enumOf(TalentArea, n.Area),
        tier: enumOf(TalentTier, n.Tier, TalentTier.Small), icon: str(n.Icon), x: num(n.X), y: num(n.Y), aura: bool(n.Aura),
        links: arr(n.Links).map(l => ref(talentKeys, l, 'talent', p)).filter(l => l !== 0),
        effects: arr(n.Effects).map(e => ({ stat: enumOf(TalentStat, obj(e).Stat), value: num(obj(e).Value) })),
      };
      if (node.id <= 0 || this.talents.has(node.id)) continue;
      this.talents.set(node.id, node);
      if (node.tier === TalentTier.Start && !this.talentStart) this.talentStart = node.id;
    }
    // Links join both ways, whichever end names them.
    for (const node of this.talents.values()) for (const l of node.links) {
      const other = this.talents.get(l);
      if (other && l !== node.id && !other.links.includes(node.id)) other.links.push(node.id);
    }
    for (const node of this.talents.values()) node.links = [...new Set(node.links)].filter(l => l !== node.id && this.talents.has(l)).sort((a, b) => a - b);
    const settings = docs['settings.json'];
    this.startingItems = arr(settings.StartingItems).map(ingredient);
    this.startingCopper = num(settings.StartingCopper);
    this.startingLevel = num(settings.StartingLevel, 1);
    this.tiles = TileCatalog.fromJson(files.get(tilesFile) ?? '{}');
    this.fingerprint = fingerprint(files);
  }

  item(id: number): ItemDefinition | undefined { return this.items.get(id); }
  npc(id: number): NpcDefinition | undefined { return this.npcs.get(id); }
  quest(id: number): QuestDefinition | undefined { return this.quests.get(id); }
  mob(type: number): MobDefinition | undefined { return this.mobs.get(type); }
  recipe(id: number): Recipe | undefined { return this.recipes.get(id); }
  entity(id: number): EntityDefinition | undefined { return this.entities.get(id); }
  talent(id: number): TalentNode | undefined { return this.talents.get(id); }
  entityByKey(key: string): EntityDefinition | undefined {
    return [...this.entities.values()].find(e => e.key.toLowerCase() === key.toLowerCase());
  }
}

/** Hash of the files as they travel: sorted by name, each without trailing whitespace (ContentBundle.Fingerprint). */
export function fingerprint(files: ReadonlyMap<string, string>): number {
  const names = [...files.keys()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  return contentHash(names.map(n => `${n}\n${files.get(n)!.trimEnd()}\n`).join(''));
}

